import React, { useState, useEffect, useCallback } from "react";
import {
  Loader2, Search, Download, ChevronUp, ChevronDown, DollarSign,
  CheckCircle2, Clock, XCircle, AlertTriangle, RefreshCw, Pencil, Check, X,
} from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const ACCENT = "#FF4D00";
const GOLD   = "#F5C842";

const STATUS_CONFIG = {
  transferred:       { label: "Paid",             color: "#22C55E", bg: "rgba(34,197,94,0.1)",    border: "rgba(34,197,94,0.3)",    Icon: CheckCircle2 },
  receipt_uploaded:  { label: "Receipt Sent",     color: "#3B82F6", bg: "rgba(59,130,246,0.1)",   border: "rgba(59,130,246,0.3)",   Icon: Clock },
  payment_pending:   { label: "Awaiting Payment", color: GOLD,      bg: "rgba(245,200,66,0.1)",   border: "rgba(245,200,66,0.35)",  Icon: Clock },
  live:              { label: "Bid Active",        color: ACCENT,    bg: "rgba(255,77,0,0.08)",    border: "rgba(255,77,0,0.25)",    Icon: DollarSign },
  annulled:          { label: "Annulled",          color: "#9333EA", bg: "rgba(147,51,234,0.08)",  border: "rgba(147,51,234,0.25)", Icon: AlertTriangle },
  declined:          { label: "Declined",          color: "#ef4444", bg: "rgba(239,68,68,0.08)",   border: "rgba(239,68,68,0.25)",   Icon: XCircle },
  expired:           { label: "Expired",           color: "#555",    bg: "rgba(255,255,255,0.04)", border: "rgba(255,255,255,0.1)",  Icon: Clock },
};

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.live;
  const Icon = cfg.Icon;
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 font-head text-[9px] font-black uppercase tracking-[0.12em] whitespace-nowrap"
      style={{ background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}` }}>
      <Icon className="w-2.5 h-2.5" />
      {cfg.label}
    </span>
  );
}

function SortBtn({ field, sortBy, sortDir, onSort }) {
  const active = sortBy === field;
  return (
    <button onClick={() => onSort(field)}
      className="inline-flex items-center gap-0.5 transition-colors"
      style={{ color: active ? ACCENT : "rgba(255,255,255,0.3)" }}>
      {active && sortDir === 1 ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
    </button>
  );
}

export function PaymentStatusPanel({ token }) {
  const [rows, setRows]           = useState([]);
  const [loading, setLoading]     = useState(true);
  const [search, setSearch]       = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortBy, setSortBy]       = useState("created_at");
  const [sortDir, setSortDir]     = useState(-1);
  const [csvExporting, setCsvExporting] = useState(false);
  const [editFeeId, setEditFeeId] = useState(null);   // bid_id being edited
  const [editFeeVal, setEditFeeVal] = useState("");
  const [editFeeSaving, setEditFeeSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.marketAdminPaymentStatus(token);
      setRows(r.bids || []);
    } catch { toast.error("Failed to load payment status"); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  function handleSort(field) {
    if (sortBy === field) {
      setSortDir((d) => d * -1);
    } else {
      setSortBy(field);
      setSortDir(-1);
    }
  }

  async function handleExportCSV() {
    setCsvExporting(true);
    try {
      const blob = await api.marketAdminExportPaymentStatusCSV(token);
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement("a");
      a.href = url; a.download = "payment_status.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("CSV export failed."); }
    finally { setCsvExporting(false); }
  }

  async function handleSaveFee(bid_id) {
    const fee = parseInt(editFeeVal, 10);
    if (isNaN(fee) || fee < 0) { toast.error("Enter a valid fee (0 or more)"); return; }
    setEditFeeSaving(true);
    try {
      await api.marketAdminPatchBidFee(token, bid_id, fee);
      toast.success(`Fee updated to $${fee.toLocaleString()}`);
      setRows((prev) => prev.map((r) => r.bid_id === bid_id ? { ...r, placement_fee: fee } : r));
      setEditFeeId(null);
      setEditFeeVal("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update fee");
    } finally { setEditFeeSaving(false); }
  }

  // Filter + sort in memory
  const q = search.toLowerCase().trim();
  const STATUS_GROUPS = ["all", "transferred", "receipt_uploaded", "payment_pending", "live", "annulled", "declined"];

  let filtered = rows.filter((r) => {
    const matchSearch = !q ||
      (r.driver_name || "").toLowerCase().includes(q) ||
      (r.carrier_name || "").toLowerCase().includes(q);
    const matchStatus = statusFilter === "all" || r.status === statusFilter;
    return matchSearch && matchStatus;
  });

  filtered = [...filtered].sort((a, b) => {
    let av = a[sortBy], bv = b[sortBy];
    if (typeof av === "string") av = av.toLowerCase();
    if (typeof bv === "string") bv = bv.toLowerCase();
    if (av < bv) return -sortDir;
    if (av > bv) return sortDir;
    return 0;
  });

  // Summary counts
  const counts = rows.reduce((acc, r) => {
    acc[r.status] = (acc[r.status] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="space-y-5" data-testid="payment-status-panel">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="font-head text-[9px] uppercase tracking-[0.4em] text-white/30 mb-0.5">Admin</p>
          <h3 className="font-head text-xl font-black uppercase tracking-[0.04em]" style={{ color: GOLD }}>
            Carrier Payment Status
          </h3>
          <p className="text-xs text-white/30 mt-0.5">All bids with real-time payment status per carrier and driver.</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button onClick={load} disabled={loading}
            data-testid="payment-status-refresh-btn"
            className="p-2 transition-colors hover:text-white/80 disabled:opacity-40"
            style={{ color: "rgba(255,255,255,0.35)", border: "1px solid rgba(255,255,255,0.1)" }}
            title="Refresh">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          </button>
          <button onClick={handleExportCSV} disabled={csvExporting || rows.length === 0}
            data-testid="payment-status-csv-btn"
            className="flex items-center gap-1.5 px-3 py-2 font-head text-[9px] uppercase tracking-[0.15em] font-black transition-all hover:brightness-110 disabled:opacity-40"
            style={{ background: "rgba(245,200,66,0.08)", color: GOLD, border: "1px solid rgba(245,200,66,0.25)" }}>
            {csvExporting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
            Export CSV
          </button>
        </div>
      </div>

      {/* Summary stat cards */}
      <div className="flex flex-wrap gap-2">
        {[
          { key: "transferred",      label: "Paid",        color: "#22C55E" },
          { key: "receipt_uploaded", label: "Receipt In",  color: "#3B82F6" },
          { key: "payment_pending",  label: "Awaiting",    color: GOLD },
          { key: "live",             label: "Active Bids", color: ACCENT },
          { key: "annulled",         label: "Annulled",    color: "#9333EA" },
          { key: "declined",         label: "Declined",    color: "#ef4444" },
        ].map(({ key, label, color }) => (
          <button key={key}
            onClick={() => setStatusFilter(statusFilter === key ? "all" : key)}
            data-testid={`payment-filter-${key}`}
            className="flex flex-col items-center px-4 py-2.5 transition-all hover:brightness-110"
            style={{
              background: statusFilter === key ? `${color}18` : "rgba(255,255,255,0.03)",
              border: `1px solid ${statusFilter === key ? `${color}40` : "rgba(255,255,255,0.08)"}`,
            }}>
            <span className="font-head text-lg font-black leading-none" style={{ color: statusFilter === key ? color : "rgba(255,255,255,0.5)" }}>
              {counts[key] || 0}
            </span>
            <span className="font-head text-[8px] uppercase tracking-[0.15em] mt-0.5" style={{ color: statusFilter === key ? color : "rgba(255,255,255,0.3)" }}>
              {label}
            </span>
          </button>
        ))}
      </div>

      {/* Search bar */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/30 pointer-events-none" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search driver or carrier…"
          data-testid="payment-status-search"
          className="w-full pl-8 pr-3 py-2 font-head text-xs bg-transparent text-white/80 placeholder-white/25 outline-none"
          style={{ border: "1px solid rgba(255,255,255,0.12)", background: "rgba(255,255,255,0.03)" }}
        />
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-5 h-5 animate-spin" style={{ color: ACCENT }} />
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center py-12" style={{ border: "1px dashed rgba(255,255,255,0.08)" }}>
          <DollarSign className="w-8 h-8 mb-2 opacity-10" />
          <p className="text-xs text-white/25 font-head uppercase tracking-[0.2em]">
            {search || statusFilter !== "all" ? "No matching records" : "No bids found"}
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto" data-testid="payment-status-table">
          <table className="w-full text-xs">
            <thead>
              <tr style={{ borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
                {[
                  { field: "driver_name",  label: "Driver" },
                  { field: "carrier_name", label: "Carrier" },
                  { field: "placement_fee", label: "Fee" },
                  { field: "status",       label: "Status" },
                  { field: "payment_option", label: "Payment Type" },
                  { field: "created_at",   label: "Bid Date" },
                ].map(({ field, label }) => (
                  <th key={field}
                    className="text-left px-4 py-3 font-head text-[9px] uppercase tracking-[0.2em] cursor-pointer select-none"
                    style={{ color: "rgba(255,255,255,0.3)" }}
                    onClick={() => handleSort(field)}>
                    <span className="inline-flex items-center gap-1">
                      {label}
                      <SortBtn field={field} sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((row, i) => (
                <tr key={row.bid_id || i}
                  data-testid={`payment-row-${row.bid_id}`}
                  style={{ borderBottom: "1px solid rgba(255,255,255,0.04)" }}
                  className="transition-colors hover:bg-white/[0.02]">
                  <td className="px-4 py-3 font-head font-bold text-white">
                    {row.driver_name || "—"}
                  </td>
                  <td className="px-4 py-3 text-white/70">{row.carrier_name || "—"}</td>
                  <td className="px-4 py-3 font-head font-black" style={{ color: GOLD }}>
                    {editFeeId === row.bid_id ? (
                      <div className="flex items-center gap-1.5">
                        <input
                          autoFocus
                          type="number" min="0"
                          value={editFeeVal}
                          onChange={(e) => setEditFeeVal(e.target.value)}
                          onKeyDown={(e) => { if (e.key === "Enter") handleSaveFee(row.bid_id); if (e.key === "Escape") { setEditFeeId(null); setEditFeeVal(""); } }}
                          className="w-20 px-2 py-1 text-xs text-white font-head"
                          style={{ background: "#111", border: "1px solid rgba(245,200,66,0.4)" }}
                        />
                        <button onClick={() => handleSaveFee(row.bid_id)} disabled={editFeeSaving}
                          className="p-1 hover:brightness-110" style={{ color: "#22C55E" }}>
                          {editFeeSaving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                        </button>
                        <button onClick={() => { setEditFeeId(null); setEditFeeVal(""); }} className="p-1 text-white/30 hover:text-white">
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 group">
                        <span>{row.placement_fee ? `$${row.placement_fee.toLocaleString()}` : "—"}</span>
                        <button
                          data-testid={`edit-fee-${row.bid_id}`}
                          title="Edit fee"
                          onClick={() => { setEditFeeId(row.bid_id); setEditFeeVal(row.placement_fee || ""); }}
                          className="opacity-0 group-hover:opacity-100 transition-opacity p-0.5"
                          style={{ color: "rgba(245,200,66,0.5)" }}>
                          <Pencil className="w-3 h-3" />
                        </button>
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge status={row.status} />
                  </td>
                  <td className="px-4 py-3 text-white/40 capitalize">
                    {(row.payment_option || "—").replace(/_/g, " ")}
                  </td>
                  <td className="px-4 py-3 text-white/35">
                    {row.created_at
                      ? new Date(row.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
                      : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="px-4 py-2.5" style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}>
            <p className="font-head text-[9px] uppercase tracking-[0.2em] text-white/25">
              {filtered.length} record{filtered.length !== 1 ? "s" : ""}
              {(search || statusFilter !== "all") && ` of ${rows.length} total`}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
